import * as document from '../actions/document';
import {DocumentMaster} from '../models/document-master';
import {DocumentInfo} from '../models/document-info';
import {KvItem} from '../../core/models/kv-item';

export interface State {
  currentFile: DocumentMaster;
  currentFileIds: number[];
  documents: DocumentMaster[];
  working: boolean;
  isDeleted: boolean;
  uploading: boolean;
  filename: string;
  currentSortField: string;
  ascending: boolean;
  downloading: number;
  fileUrl: string;
}

export const initialState: State = {
  currentFile: new DocumentMaster(0),
  currentFileIds: new Array<number>(),
  documents: new Array<DocumentMaster>(),
  working: false,
  isDeleted: false,
  uploading: false,
  filename: '',
  currentSortField: '',
  ascending: true,
  downloading: 0,
  fileUrl: ''
};

export function reducer(state = initialState, action: any): State {
  let newState: State;
  let doc: DocumentMaster;
  switch (action.type) {

    case document.UPLOAD_FILE:
      newState = Object.assign({}, state);
      const file = action.payload as DocumentInfo;
      doc = Object.assign({}, initialState.currentFile);
      doc.filename = file.filename;
      doc.mimeType = file.type;
      doc.fileExtension = file.extension;
      newState.currentFile = doc;
      newState.working = true;
      newState.uploading = true;
      return newState;

    case document.UPLOAD_FILE_COMPLETE:
      newState = Object.assign({}, state);
      doc = Object.assign({}, newState.currentFile);
      const item = action.payload as KvItem;
      doc.fileId = item.id;
      doc.size = item.parentId;
      doc.sizeString = '';
      newState.currentFile = doc;
      newState.working = false;
      newState.uploading = false;
      return newState;

    case document.ATTACH_METADATA_TO_FILE:
      newState = Object.assign({}, state);
      newState.currentFile = action.payload as DocumentMaster;
      newState.working = true;
      return newState;

    case document.ATTACH_METADATA_TO_FILE_COMPLETE:
      newState = Object.assign({}, state);
      doc = Object.assign({}, newState.currentFile);
      doc.id = action.payload as number;
      newState.currentFile = doc;
      newState.working = false;
      return newState;

    case document.RETRIEVE_METADATA:
      newState = Object.assign({}, state);
      newState.working = true;
      newState.currentFileIds = [action.payload];
      newState.currentFile = initialState.currentFile;
      return newState;

    case document.RETRIEVE_METADATA_COMPLETE:
      newState = Object.assign({}, state);
      newState.working = false;
      newState.currentFile = action.payload;
      return newState;

    case document.RETRIEVE_METADATA_FOR_IDS:
      newState = Object.assign({}, state);
      Object.assign({}, initialState.currentFile);
      newState.working = true;
      newState.currentFileIds = action.payload;
      return newState;

    case document.RETRIEVE_METADATA_FOR_IDS_COMPLETE:
      newState = Object.assign({}, state);
      newState.working = false;
      newState.documents = action.payload;
      newState.currentFile = Object.assign({}, initialState.currentFile);
      return newState;

    case document.RETRIEVE_ALL_ACTIVE:
      newState = Object.assign({}, state);
      newState.working = true;
      newState.ascending = initialState.ascending;
      newState.currentSortField = initialState.currentSortField;
      newState.isDeleted = false;
      return newState;

    case document.RETRIEVE_ALL_ACTIVE_COMPLETE:
      newState = Object.assign({}, state);
      newState.working = false;
      newState.documents = action.payload;
      newState.currentFile = Object.assign({}, initialState.currentFile);
      return newState;

    case document.RETRIEVE_ALL_DELETED:
      newState = Object.assign({}, state);
      newState.working = true;
      return newState;

    case document.RETRIEVE_ALL_DELETED_COMPLETE:
      newState = Object.assign({}, state);
      newState.working = false;
      newState.documents = action.payload;
      newState.isDeleted = true;
      newState.currentFile = Object.assign({}, initialState.currentFile);
      return newState;

    case document.GET_FILE_VIEW_URL:
      newState = Object.assign({}, state);
      newState.working = true;
      return newState;

    case document.GET_FILE_VIEW_URL_COMPLETE:
      newState = Object.assign({}, state);
      newState.working = false;
      newState.fileUrl = action.payload;
      return newState;

    case document.CLEAR_FILE_VIEW_URL:
      newState = Object.assign({}, state);
      newState.fileUrl = '';
      return newState;

    case document.REMOVE_DOCUMENT:
      newState = Object.assign({}, state);
      newState.currentFileIds = [action.payload];
      newState.working = true;
      return newState;

    case document.REMOVE_DOCUMENT_COMPLETE:
      newState = Object.assign({}, state);
      newState.working = true;
      newState.currentFileIds = [];
      return newState;

    case document.DOWNLOAD_FILE:
      newState = Object.assign({}, state);
      newState.downloading = action.payload.id;
      newState.working = true;
      return newState;

    case document.DOWNLOAD_FILE_COMPLETE:
      newState = Object.assign({}, state);
      newState.downloading = 0;
      newState.working = false;
      return newState;

    case 'ERROR':
      newState = Object.assign({}, state);
      newState.working = false;
      newState.uploading = false;
      newState.downloading = 0;
      return newState;

    case document.RESET_DOCUMENT_UPLOAD:
      newState = Object.assign({}, state);
      newState.currentFile = Object.assign({}, initialState.currentFile);
      newState.uploading = false;
      return newState;

    case document.SORT_DOCUMENT_LIST:
      newState = Object.assign({}, state);
      const currentList = newState.documents.slice();
      const field = action.payload as string;
      const firstItem = currentList[0] as DocumentMaster;
      if (isNaN(firstItem[field])) {
        currentList.sort((a, b) => a[field] - b[field]);
      } else {
        currentList.sort((a, b) => a[field].localeCompare(b[field]));
      }
      newState.currentSortField = field;
      if (field === state.currentSortField) {
        newState.ascending = !newState.ascending;
      } else {
        newState.ascending = true;
      }
      if (newState.ascending === false) {
        currentList.reverse();
      }
      newState.documents = currentList;
      return newState;

    default:
      newState = Object.assign({}, state);
      return newState;
  }
}

export const getCurrentFile = (state: State) => state.currentFile;
export const getCurrentFileIds = (state: State) => state.currentFileIds;
export const getDocuments = (state: State) => state.documents;
export const getWorking = (state: State) => state.working;
export const getIsDeleted = (state: State) => state.isDeleted;
export const getUploading = (state: State) => state.uploading;
export const getFilename = (state: State) => state.filename;
export const getAscending = (state: State) => state.ascending;
export const getSortField = (state: State) => state.currentSortField;
export const getDownloading = (state: State) => state.downloading;
export const getUrl = (state: State) => state.fileUrl;



// WEBPACK FOOTER //
// C:/Users/jstan/Projects/usda-document-client/src/app/modules/document/reducers/document.ts